import { Link } from "react-router-dom";
import { Phone, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const galleryImages = [
  {
    src: "https://images.pexels.com/photos/4239031/pexels-photo-4239031.jpeg?auto=compress&cs=tinysrgb&w=800",
    alt: "Professional house cleaning service",
    caption: "Professional House Cleaning",
  },
  {
    src: "https://images.pexels.com/photos/4239091/pexels-photo-4239091.jpeg?auto=compress&cs=tinysrgb&w=800",
    alt: "Sparkling clean modern kitchen",
    caption: "Sparkling Kitchens",
  },
  {
    src: "https://images.pexels.com/photos/4239036/pexels-photo-4239036.jpeg?auto=compress&cs=tinysrgb&w=800",
    alt: "Clean bathroom sanitization",
    caption: "Sanitized Bathrooms",
  },
  {
    src: "https://images.pexels.com/photos/4107120/pexels-photo-4107120.jpeg?auto=compress&cs=tinysrgb&w=800",
    alt: "Move-in move-out cleaning",
    caption: "Move-In/Move-Out",
  },
  {
    src: "https://images.pexels.com/photos/4239146/pexels-photo-4239146.jpeg?auto=compress&cs=tinysrgb&w=800",
    alt: "Professional cleaning supplies",
    caption: "Professional Equipment",
  },
  {
    src: "https://images.pexels.com/photos/4239035/pexels-photo-4239035.jpeg?auto=compress&cs=tinysrgb&w=800",
    alt: "Kitchen deep cleaning",
    caption: "Deep Cleaned Kitchens",
  },
  {
    src: "https://images.pexels.com/photos/4239130/pexels-photo-4239130.jpeg?auto=compress&cs=tinysrgb&w=800",
    alt: "Organized clean space",
    caption: "Organized Spaces",
  },
  {
    src: "https://images.pexels.com/photos/4108715/pexels-photo-4108715.jpeg?auto=compress&cs=tinysrgb&w=800",
    alt: "Clean bathroom details",
    caption: "Bathroom Detailing",
  },
];

export default function Gallery() {
  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-b from-sky-50 to-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Our Work Gallery
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              See the West Texas Sparkle difference for yourself. Browse through examples of our 
              cleaning work in homes and offices throughout the Lubbock area.
            </p>
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 md:py-24 bg-white" data-testid="gallery-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {galleryImages.map((image, index) => (
              <div 
                key={index} 
                className="group relative overflow-hidden rounded-2xl bg-slate-100"
                data-testid={`gallery-image-${index}`}
              >
                <div className="aspect-square">
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover gallery-image"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <p className="text-white font-medium">{image.caption}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Before/After Note */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-4">
            Every Home Tells a Story
          </h2>
          <p className="text-slate-600 leading-relaxed mb-8">
            From dusty corners to sparkling surfaces, we take pride in transforming spaces. 
            Our team approaches every job with the same dedication, whether it's a quick 
            touch-up or a complete deep clean.
          </p>
          <p className="text-slate-500 text-sm">
            Photos represent typical results. Your home's transformation awaits!
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-sky-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            Ready for Your Own Transformation?
          </h2>
          <p className="text-sky-100 text-lg mb-8 max-w-2xl mx-auto">
            Let us show you what West Texas Sparkle can do for your home or office. 
            Contact us today for a free quote!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:8065558123">
              <Button 
                size="lg" 
                className="w-full sm:w-auto bg-white text-sky-700 hover:bg-sky-50 rounded-full h-14 px-8 text-lg font-semibold shadow-lg"
                data-testid="gallery-cta-call"
              >
                <Phone className="w-5 h-5 mr-2" />
                (806) 555-8123
              </Button>
            </a>
            <Link to="/contact">
              <Button 
                size="lg" 
                variant="outline"
                className="w-full sm:w-auto border-2 border-white/30 bg-transparent hover:bg-white/10 text-white rounded-full h-14 px-8 text-lg font-semibold"
                data-testid="gallery-cta-quote"
              >
                Request a Quote
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
